import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiUpload, apiGet, apiDelete } from "../lib/api";
import { COLORS } from "../lib/colors";
import BulkUpload from "./BulkUpload";

export default function AlumniUpload({ programmeId }) {
  const { session } = useAuth();
  const token = session?.access_token;
  const [tab, setTab] = useState("list");
  const [file, setFile] = useState(null);
  const [gradYear, setGradYear] = useState(2023);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [alumni, setAlumni] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const loadAlumni = async () => {
    if (!programmeId || !token) return;
    setListLoading(true);
    try {
      const data = await apiGet(`/alumni/${programmeId}/list`, token);
      setAlumni(data.alumni || []);
    } catch {
      setAlumni([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadAlumni(); }, [programmeId, token]);

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete ${name}?`)) return;
    try {
      await apiDelete(`/alumni/cv/${id}`, token);
      setAlumni((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const handleSingle = async () => {
    if (!file || !programmeId) return;
    setLoading(true);
    setResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await apiUpload(`/upload/alumni/${programmeId}?graduation_year=${gradYear}`, formData, token);
      setResult(data);
      loadAlumni();
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  if (!programmeId) {
    return (
      <div style={{
        backgroundColor: COLORS.surface, borderRadius: 12,
        border: `1px solid ${COLORS.border}`, padding: 40, textAlign: "center",
      }}>
        <p style={{ fontSize: 14, color: COLORS.textMuted }}>Select a programme in Setup first.</p>
      </div>
    );
  }

  const tabs = [
    { key: "list", label: `CVs (${alumni.length})` },
    { key: "bulk", label: "Bulk Upload" },
    { key: "single", label: "Single Upload" },
  ];

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Alumni CVs</h2>
      <p style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 24 }}>
        Upload and manage alumni CVs. Analyzes employment speed, employer quality, career progression, and international reach.
      </p>

      <TabBar tabs={tabs} tab={tab} setTab={setTab} />

      {tab === "list" && (
        <AlumniTable alumni={alumni} loading={listLoading} onDelete={handleDelete} onRefresh={loadAlumni} />
      )}

      {tab === "bulk" && (
        <div>
          <GradYearInput value={gradYear} onChange={setGradYear} />
          <BulkUpload uploadPath="alumni" entityId={programmeId} extraParams={{ graduation_year: gradYear }} onComplete={loadAlumni} />
        </div>
      )}

      {tab === "single" && (
        <div style={{
          backgroundColor: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`, padding: 24,
        }}>
          <GradYearInput value={gradYear} onChange={setGradYear} />
          <input type="file" accept=".pdf,.docx,.txt" onChange={(e) => setFile(e.target.files[0])} style={{ marginBottom: 16 }} />
          <br />
          <button
            onClick={handleSingle}
            disabled={!file || loading}
            style={{
              padding: "10px 24px", fontSize: 14, fontWeight: 600,
              backgroundColor: file && !loading ? COLORS.accent : COLORS.border,
              color: file && !loading ? "#fff" : COLORS.textMuted,
              border: "none", borderRadius: 8, cursor: file && !loading ? "pointer" : "not-allowed",
            }}
          >{loading ? "Processing..." : "Process CV"}</button>

          {result && !result.error && (
            <div style={{ marginTop: 20, padding: 16, borderRadius: 12, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surfaceAlt }}>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>Score</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.accent }}>{result.efmd_score}/100</div>
              {result.employer && <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>Current Employer: {result.employer}</div>}
              {result.strengths?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {result.strengths.map((s, i) => <div key={i} style={{ fontSize: 13, color: COLORS.green }}>+ {s}</div>)}
                </div>
              )}
            </div>
          )}
          {result?.error && (
            <div style={{ marginTop: 16, backgroundColor: COLORS.redLight, color: COLORS.red, padding: "8px 12px", borderRadius: 8, fontSize: 13 }}>
              {result.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AlumniTable({ alumni, loading, onDelete, onRefresh }) {
  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 14 }}>Loading alumni CVs...</div>;
  }

  if (alumni.length === 0) {
    return (
      <div style={{
        backgroundColor: COLORS.surface, borderRadius: 12,
        border: `1px solid ${COLORS.border}`, padding: 40, textAlign: "center",
      }}>
        <p style={{ fontSize: 14, color: COLORS.textMuted }}>No alumni CVs uploaded yet. Use Bulk Upload or Single Upload to add CVs.</p>
      </div>
    );
  }

  const recommended = alumni.filter((a) => a.include_recommended);
  const avgScore = Math.round(alumni.reduce((sum, a) => sum + (a.efmd_score || 0), 0) / alumni.length);
  const abroad = alumni.filter((a) => a.working_abroad).length;

  return (
    <div>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <StatCard label="Total CVs" value={alumni.length} />
        <StatCard label="Recommended" value={recommended.length} color={COLORS.green} />
        <StatCard label="Avg Score" value={avgScore} />
        <StatCard label="Working Abroad" value={abroad} color={COLORS.purple} />
        <div style={{ flex: 1 }} />
        <button onClick={onRefresh} style={{
          padding: "6px 14px", fontSize: 12, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, backgroundColor: COLORS.surface, color: COLORS.textSecondary, cursor: "pointer",
        }}>Refresh</button>
      </div>

      <div style={{
        backgroundColor: COLORS.surface, borderRadius: 12,
        border: `1px solid ${COLORS.border}`, overflow: "hidden",
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: COLORS.surfaceAlt, borderBottom: `1px solid ${COLORS.border}` }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Employer</th>
              <th style={thStyle}>Job Title</th>
              <th style={thStyle}>Career Level</th>
              <th style={thStyle}>Grad Year</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Abroad</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Score</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Include</th>
              <th style={{ ...thStyle, width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {alumni.map((a) => (
              <AlumniRow key={a.id} a={a} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CvDownloadBtn({ cvType, cvId, name }) {
  const { session } = useAuth();
  const handleDownload = async (e) => {
    e.stopPropagation();
    const API_URL = import.meta.env.VITE_API_URL || "https://api-calibre.edtechsolutions.io";
    try {
      const res = await fetch(`${API_URL}/cv/${cvType}/${cvId}/download`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name.replace(/\s+/g, '_')}_CV.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert("CV download failed: " + err.message); }
  };
  return (
    <button onClick={handleDownload} style={{
      padding: "4px 12px", fontSize: 11, border: `1px solid #58a6ff`,
      borderRadius: 4, background: "none", color: '#58a6ff', cursor: "pointer",
    }}>↓ CV</button>
  );
}

function AlumniRow({ a, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const isSenior = ["senior", "executive", "c-level", "partner", "director", "vp"].some(
    l => (a.career_level || "").toLowerCase().includes(l) || (a.current_job_title || "").toLowerCase().includes(l)
  );

  const criteria = [
    { code: "4.2.1", name: "Employment", value: a.current_employer ? `At ${a.current_employer}` : "Not employed", ok: !!a.current_employer },
    { code: "4.2.1", name: "Role", value: a.current_job_title || "—", ok: !!a.current_job_title },
    { code: "4.2.1", name: "Career Level", value: a.career_level || "—", ok: isSenior },
    { code: "4.2.1", name: "International", value: a.working_abroad ? "Working abroad" : "Domestic", ok: !!a.working_abroad },
    { code: "4.2.1", name: "Graduated", value: a.graduation_year ? String(a.graduation_year) : "—", ok: null },
  ];

  return (
    <>
      <tr onClick={() => setExpanded(!expanded)} style={{ borderBottom: `1px solid ${COLORS.borderLight}`, cursor: "pointer" }}>
        <td style={tdStyle}><span style={{ fontWeight: 500 }}>{a.full_name}</span></td>
        <td style={tdStyle}>{a.current_employer || "—"}</td>
        <td style={tdStyle}>{a.current_job_title || "—"}</td>
        <td style={tdStyle}>{a.career_level || "—"}</td>
        <td style={tdStyle}>{a.graduation_year || "—"}</td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          {a.working_abroad ? <span style={{ color: COLORS.green }}>Yes</span> : "—"}
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}><ScoreBadge score={a.efmd_score} /></td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          {a.include_recommended ? <span style={{ color: COLORS.green, fontWeight: 600 }}>Yes</span> : <span style={{ color: COLORS.orange }}>Review</span>}
        </td>
        <td style={tdStyle}><span style={{ color: COLORS.textMuted, fontSize: 10 }}>{expanded ? "▲" : "▼"}</span></td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} style={{ padding: 0 }}>
            <div style={{ background: COLORS.surfaceAlt, padding: "8px 16px", borderBottom: `1px solid ${COLORS.border}` }}>
              {criteria.map((c, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", padding: "6px 8px",
                  borderBottom: i < criteria.length - 1 ? `1px solid ${COLORS.borderLight}` : "none", fontSize: 13,
                }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                    background: c.ok === true ? COLORS.green : c.ok === false ? COLORS.orange : COLORS.border,
                    color: c.ok != null ? "#fff" : COLORS.textMuted,
                  }}>{c.ok === true ? "✓" : c.ok === false ? "!" : "—"}</span>
                  <span style={{ width: 50, color: COLORS.textMuted, fontFamily: "monospace", fontSize: 11, marginLeft: 8 }}>{c.code}</span>
                  <span style={{ width: 120, color: COLORS.textSecondary }}>{c.name}</span>
                  <span style={{ fontWeight: 600, color: COLORS.text }}>{c.value}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 6 }}>
                <CvDownloadBtn cvType="alumni" cvId={a.id} name={a.full_name} />
                <button onClick={(e) => { e.stopPropagation(); onDelete(a.id, a.full_name); }}
                  style={{ padding: "4px 12px", fontSize: 11, border: `1px solid ${COLORS.red}`, borderRadius: 4, background: "none", color: COLORS.red, cursor: "pointer" }}>Delete</button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function GradYearInput({ value, onChange }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: COLORS.textSecondary, marginRight: 8 }}>Graduation Year:</label>
      <input
        type="number" min={2015} max={2030} value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        style={{ padding: "6px 10px", fontSize: 14, border: `1px solid ${COLORS.border}`, borderRadius: 6, width: 80 }}
      />
    </div>
  );
}

function TabBar({ tabs, tab, setTab }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => setTab(t.key)}
          style={{
            padding: "8px 20px", fontSize: 13, fontWeight: 500, borderRadius: 8,
            border: `1px solid ${tab === t.key ? COLORS.accent : COLORS.border}`,
            backgroundColor: tab === t.key ? COLORS.accentLight : COLORS.surface,
            color: tab === t.key ? COLORS.accent : COLORS.textSecondary,
            cursor: "pointer",
          }}
        >{t.label}</button>
      ))}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`,
      borderRadius: 8, padding: "10px 16px", minWidth: 80,
    }}>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || COLORS.text }}>{value}</div>
    </div>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 65 ? COLORS.green : score >= 45 ? COLORS.orange : COLORS.red;
  const bg = score >= 65 ? COLORS.greenLight : score >= 45 ? COLORS.orangeLight : COLORS.redLight;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      fontSize: 12, fontWeight: 600, color, backgroundColor: bg,
    }}>{score}</span>
  );
}

const thStyle = {
  textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 600,
  color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px",
};

const tdStyle = {
  padding: "10px 12px", color: COLORS.text,
};
